import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Handles visibility selection for both form-based and auto-save pickers.
//
// Form-based mode (non-auto-save): uses a hidden gidsField that gets submitted with the form.
// Auto-save mode: uses updateUrl value to PATCH visibility changes immediately via fetch.
//
// "Select Organizations" opens a dynamic modal. In form mode, the modal dispatches
// a "visibility-orgs-selected" event that this controller listens for.
export default class extends Controller {
  static targets = ["gidsField", "buttonText"]
  static values = {
    organizationGid: String,
    networkGid: String,
    updateUrl: String // When present, enables auto-save mode
  }

  connect() {
    this.handleOrgsSelected = this.onOrgsSelected.bind(this)
    window.addEventListener("visibility-orgs-selected", this.handleOrgsSelected)
  }

  disconnect() {
    window.removeEventListener("visibility-orgs-selected", this.handleOrgsSelected)
  }

  // Form-based: set hidden field to current org GID
  selectOrganization(event) {
    event.preventDefault()
    this.gidsFieldTarget.value = this.organizationGidValue
    this.updateActiveState(event.currentTarget)
  }

  // Form-based: set hidden field to network GID
  selectNetwork(event) {
    event.preventDefault()
    this.gidsFieldTarget.value = this.networkGidValue
    this.updateActiveState(event.currentTarget)
  }

  // Auto-save: select org and immediately PATCH
  selectOrganizationAndSubmit(event) {
    event.preventDefault()
    this.updateActiveState(event.currentTarget)
    this.submitVisibility(this.organizationGidValue)
  }

  // Auto-save: select network and immediately PATCH
  selectNetworkAndSubmit(event) {
    event.preventDefault()
    this.updateActiveState(event.currentTarget)
    this.submitVisibility(this.networkGidValue)
  }

  // Callback from org picker modal (form-based mode)
  onOrgsSelected(event) {
    const { gids } = event.detail
    if (this.hasGidsFieldTarget) {
      this.gidsFieldTarget.value = gids
    }
    this.setSelectOrgsActive()
  }

  updateActiveState(clickedElement) {
    this.element.querySelectorAll(".dropdown-content li").forEach(item => {
      item.classList.remove("active")
    })

    clickedElement.closest("li").classList.add("active")

    const selectedText = clickedElement.textContent.trim()
    if (this.hasButtonTextTarget) {
      const textNode = Array.from(this.buttonTextTarget.childNodes).find(n => n.nodeType === Node.TEXT_NODE)
      if (textNode) {
        textNode.textContent = selectedText
      }
    }
  }

  setSelectOrgsActive() {
    this.element.querySelectorAll(".dropdown-content li").forEach(item => {
      item.classList.remove("active")
    })
    const selectOrgsItem = this.element.querySelector("[data-select-orgs]")
    if (selectOrgsItem) {
      selectOrgsItem.closest("li").classList.add("active")
      const text = selectOrgsItem.textContent.trim()
      if (this.hasButtonTextTarget) {
        const textNode = Array.from(this.buttonTextTarget.childNodes).find(n => n.nodeType === Node.TEXT_NODE)
        if (textNode) textNode.textContent = text
      }
    }
  }

  // PATCH visibility_target_gids to the update URL via Turbo Stream
  submitVisibility(gids) {
    if (!this.updateUrlValue) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = this.updateUrlValue

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      },
      body: `visibility_target_gids=${encodeURIComponent(gids)}`
    }).then(response => {
      if (response.ok) {
        return response.text()
      }
    }).then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }
    })
  }
}
